import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  PersonAdd,
  Work,
  Group,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import AddShiftModal from '../../components/modals/AddShiftModal';
import DashboardSidebar from '../../components/DashboardSidebar';
import { validateAuth } from '../../lib/Auth/Auth';
import { deleteShift, getShift, getShifts, putShift } from '../../lib/connector/shiftQueries';
import InviteEmployeeModal from '../../components/modals/InviteEmployeeModal';
import Holidays from 'date-holidays'
import { getUserPool } from '../../lib/pools/pools';
import ManagePositionsModal from '../../components/modals/ManagePositionsModal';
import { useNavigate } from 'react-router-dom';
import { checkAccessLevel, logout } from '../../lib/authenticate';
import { deleteUserNotificationSettings, getUser, putUser } from '../../lib/connector/userQueries';
import { getPositions, putPosition } from '../../lib/connector/positionQueries';
import ManageShiftModal from '../../components/modals/ManageShiftModal/ManageShiftModal';
import ManageEmployeesModal from '../../components/modals/ManageEmployeesModal';
import { deleteScheduleUser, getSchedules, getScheduleUser, putSchedule, putScheduleUser, submitSchedule } from '../../lib/connector/scheduleQueries';
import MainHeader from '../../components/MainHeader';
import { darkTheme, lightTheme, useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import EmployeeDayModal from '../../components/modals/EmployeeDayModal';
import SwapShiftModal from '../../components/modals/SwapShiftModal';
import { getAssignments, putAssignment } from '../../lib/connector/assignmentsQueries';
import ManagerApprovalModal from '../../components/modals/ManagerApprovalModal';
import { BaseRequest, Request } from '../../components/modals/ManagerApprovalModal/ManagerApprovalModal';
import QuickActionSidebar from '../../components/QuickActionSidebar/QuickActionSidebar';
import RequestOffModal from '../../components/modals/RequestOffModal/RequestOffModal';
import ScheduleDeadlineSnackbar from '../../components/ScheduleDeadlineSnackbar';
import ScheduleViewControls from '../../components/ScheduleViewControls/ScheduleViewControls';
import ScheduleLegend from '../../components/ScheduleLegend/ScheduleLegend';
import { getAvailabilities, getDefaultAvailabilities, putAvailability, putDefaultAvailability } from '../../lib/connector/availabilityQueries';
import AvailabilitySubmissionModal from '../../components/modals/AvailabilitySubmissionModal';
import DefaultAvailabilitySubmissionModal from '../../components/modals/DefaultAvailabilitySubmissionModal';
import { useToast } from '../../components/modals/ToastProvider/ToastProvider';
import { getAuth } from '../../contexts/authStore';
import { getInviteRequests, getRequests, putInviteRequest, putNotificationRequest, putRequest } from '../../lib/connector/requestQueries';
import WeekView from './WeekView';
import MonthView from './MonthView';
import IncompleteDefaultAvailabilityModal from '../../components/modals/IncompleteDefaultAvailabilityModal/IncompleteDefaultAvailabilityModal';
import { getReview, putReview } from '../../lib/connector/reviewQueries';
import SubmitReviewModal from '../../components/modals/SubmitReviewModal';

export interface Schedule {
  scheduleId: string;
  userId: string;
  name: string;
  favorite: boolean;
  color: string;
  shiftsCount: number;
  link: string;
  employees: string[];
  lastActiveScheduleDate: string;
  activeScheduleFrequency: string;
  allowSwapWithoutApproval: boolean;
  allowPickUpWithoutApproval: boolean;
  viewOtherStaff: boolean;
  availabilityScheduleRequiredTime: number;
  activeScheduleDate: string;
  lastSubmitted: string;
  primarySchedule: boolean;
}

export interface Employee {
  userId: string;
  name: string;
  accessLevel: 'admin' | 'manager' | 'staff';
}

export default function Dashboard() {
  const { isDarkMode } = useThemeContext();
  const toast = useToast();
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState(document.documentElement.clientWidth > 700 ? 'week' : 'month');
  const [currentWeek, setCurrentWeek] = useState<moment.Moment>(moment());
  const [daysOfTheCurrentWeek, setDaysOfTheCurrentWeek] = useState<moment.Moment[]>([]);
  const [shifts, setShifts] = useState<Record<string, any>[]>([]);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const [showAddShiftModal, setShowAddShiftModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showRequestOffModal, setShowRequestOffModal] = useState(false);
  const [holidays, setHolidays] = useState<Record<string, any>[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null as Schedule | null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedShift, setSelectedShift] = useState(null as any | null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showManageShiftModal, setShowManageShiftModal] = useState(false);
  const [selectedDayForAssignment, setSelectedDayForAssignment] = useState<moment.Moment>();
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showDefaultAvailabitlityModal, setShowDefaultAvailabilityModal] = useState(false);
  const [showManagePositionsModal, setShowManagePositionsModal] = useState(false);
  const [nextShiftExpanded, setNextShiftExpanded] = useState(false);
  const [positions, setPositions] = useState<Record<string, any>[]>([]);
  const [defaultAvailabilities, setDefaultAvailabilities] = useState<Record<string, any>[]>([]);
  const [availabilities, setAvailabilities] = useState<Record<string, any>[]>([]);
  const [showManageEmployees, setShowManageEmployees] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [availabilityMode, setAvailabilityMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());
  const [shiftsForDay, setShiftsForDay] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<Record<string, any>[]>([]);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [showDeadline, setShowDeadline] = useState(false);
  const [accessLevel, setAccessLevel] = useState<string | null>(null);
  const [requests, setRequests] = useState<Request[] | null>(null);
  const [addShiftFormData, setAddShiftFormData] = useState<{
    shiftName: string;
    daysOfWeek: string[];
    startTime: string;
    endTime: string;
    position: string;
    staffNeeded: number;
    isRecurring: boolean;
    shiftDate: string;
    notes: string;
  }>({
    shiftName: '',
    daysOfWeek: [],
    startTime: '',
    endTime: '',
    position: '',
    staffNeeded: 1,
    isRecurring: true,
    shiftDate: moment().format('YYYY-MM-DD'),
    notes: ''
  });
  const [showIncompleteAvailabilityModal, setShowIncompleteAvailabilityModal] = useState(false);
  const [showRequestReviewModal, setShowRequestReviewModal] = useState(false);

  // Legend filter states
  const [legendFilters, setLegendFilters] = useState<Record<string, boolean>>({
    fullyStaffed: true,
    understaffed: true,
    unstaffed: true,
    unfinalized: true,
    holiday: true,
    nextSchedule: true,
    available: true,
    unavailable: true,
    semiAvailable: true,
    today: true,
  });

  const toggleLegendFilter = (filterKey: string) => {
    setLegendFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const shouldShowDay = (dayDate: moment.Moment, isHoliday: boolean, isSubmitNextDate: boolean): boolean => {

    let cutoff = moment(selectedSchedule?.activeScheduleDate).add(selectedSchedule?.activeScheduleFrequency === "biweekly"
      ? 2 : 1, selectedSchedule?.activeScheduleFrequency !== "monthly" ? "week" : "month")
    if (selectedSchedule?.activeScheduleFrequency === "monthly") {
      const activeScheduleMonth = moment(selectedSchedule?.activeScheduleDate).month()+1
      const activeScheduleYear = moment(selectedSchedule?.activeScheduleDate).year()
      const nextMonth = activeScheduleMonth + 1 > 11 ? 0 : activeScheduleMonth + 1
      const nextMonthYear = nextMonth === 0 ? activeScheduleYear + 1 : activeScheduleYear
      cutoff = moment(`${nextMonthYear}-${(nextMonth).toString().padStart(2, '0')}-01`).endOf('month');
    }
    if (dayDate.isAfter(cutoff)) {
      return false;
    }
    if (availabilityMode) {
      // In availability mode, check availability filters
      const availabilityColor = getAvailabilityColor(isDarkMode, dayDate.format("YYYY-MM-DD"));
      const isAvailable = availabilityColor.includes('success');
      const isUnavailable = availabilityColor.includes('error');
      const isSemiAvailable = availabilityColor.includes('warning');

      if (isAvailable && !legendFilters.available) return false;
      if (isUnavailable && !legendFilters.unavailable) return false;
      if (isSemiAvailable && !legendFilters.semiAvailable) return false;
      if (isHoliday && !legendFilters.holiday) return false;
      if (dayDate.isSame(moment(), 'day') && !legendFilters.today) return false;
    } else {
      // In schedule mode
      if (isSubmitNextDate && !legendFilters.nextSchedule) return false;
      if (isHoliday && !legendFilters.holiday) return false;
    }
    return true;
  };

  const filteredShifts = shifts.filter(shift => {
    if (legendFilters.fullyStaffed && shift.staffNeeded <= shift.assignedStaff.length && shift.assignedStaff.length !== 0) {
      return shift;
    } else if (legendFilters.understaffed && shift.assignedStaff.length > 0 && shift.staffNeeded > shift.assignedStaff.length) {
      return shift;
    } else if (legendFilters.unstaffed && shift.assignedStaff.length === 0) {
      return shift;
    } else {
      return false
    }
  })

  React.useEffect(() => {
    getUser(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
      setUserData(response[0]);
      putUser(response[0].userId, response[0].accountType, response[0].name, response[0].email, response[0].phone, response[0].created, response[0].archived, response[0].primarySchedule, new Date().toISOString())
    })
  }, [])

  // Check if user should see review prompt
  React.useEffect(() => {
    const checkReviewEligibility = async () => {
      if (!userData.userId || !userData.created) return;

      try {
        // Check if user dismissed the modal in this session
        const wasDismissed = sessionStorage.getItem(`reviewModalDismissed_${userData.userId}`);
        if (wasDismissed) return;

        // Check if account is at least 7 days old
        const accountAgeInDays = moment().diff(moment(userData.created), 'days');
        if (accountAgeInDays < 7) return;

        // Check if user has already left a review
        const existingReview = await getReview(userData.userId);
        if (existingReview && existingReview.length > 0) return; // User already has a review

        // Show the review modal
        setShowRequestReviewModal(true);
      } catch (error) {
        console.log('Error checking review eligibility:', error);
      }
    };

    checkReviewEligibility();
  }, [userData]);

  React.useEffect(() => {
    moment(currentWeek).startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(moment(currentWeek).startOf('week').add(i, 'days'));
    }
    setDaysOfTheCurrentWeek(days);

    var hd = new Holidays()
    hd.init('US', { types: ['public'] })
    setHolidays(hd.getHolidays(moment(currentWeek).format("YYYY")))
  }, [currentWeek])

  React.useEffect(() => {
    if (validateAuth() && selectedSchedule) {
      getShifts(selectedSchedule.scheduleId).then((response) => {
        setShifts(response)
      })
      getPositions(selectedSchedule.scheduleId).then((positions) => {
        setPositions(positions)
      })
      getAssignments(selectedSchedule.scheduleId, undefined, moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')).then((response) => {
        setAssignments(response)
      })
      getRequests(selectedSchedule.scheduleId).then((response) => {
        getInviteRequests(selectedSchedule.scheduleId).then((response2) => {
          const mergedRequests = [...(response ?? []), ...(response2 ?? [])]
          mergedRequests.map((request: any) => {
            request.requestDetails = JSON.parse(request.requestDetails)
          })

          const currentEmployeeData = employees?.filter((e) => e.userId === userData.userId)

          if (currentEmployeeData !== undefined) {
            if (currentEmployeeData[0]?.accessLevel === "staff") {
              setRequests(mergedRequests.filter((r: any) => r.requesterId === userData.userId))
            } else {
              setRequests(mergedRequests)
            }
          } else {
            setRequests(null)
          }
        });
      })

      setEmployees([]);
      const allSelectedScheduleUsers = [...(selectedSchedule.employees ?? []), selectedSchedule.userId];
      allSelectedScheduleUsers.map((employeeId: string) => {
        getScheduleUser(employeeId).then((response) => {
          setEmployees((prev) => [...prev, response[0]]);
          if (employeeId === getUserPool()?.getCurrentUser()?.getUsername()) {
            setAccessLevel(response[0].accessLevel)
            if (checkAccessLevel(response[0].accessLevel)) {
              getAvailabilities(selectedSchedule.scheduleId, moment().startOf('month').format('YYYY-MM-DD'),
                moment().endOf('month').format('YYYY-MM-DD')).then((response) => {
                  setAvailabilities(response)
                })
              getDefaultAvailabilities(selectedSchedule.scheduleId).then((response) => {
                setDefaultAvailabilities(response)
              })
              if (moment().diff(moment(selectedSchedule?.activeScheduleDate).subtract(5, 'day'), 'days') > 0) {
                setShowDeadline(true);
              }
            } else {
              getAvailabilities(selectedSchedule.scheduleId, moment().startOf('month').format('YYYY-MM-DD') + "#" + userData.userId,
                moment().endOf('month').format('YYYY-MM-DD') + "#" + userData.userId).then((response) => {
                  setAvailabilities(response)
                })
              getDefaultAvailabilities(selectedSchedule.scheduleId, userData.userId).then((response) => {
                setDefaultAvailabilities(response)
              })
            }
          }
        });
      })
    }
  }, [selectedSchedule])

  // Update shiftsForDay when selectedDate or shifts change
  React.useEffect(() => {
    const selectedDateFormatted = selectedDate.format('YYYY-MM-DD');
    const selectedDayOfWeek = selectedDate.format('dddd').toLowerCase();

    const filteredShifts = shifts.filter((s: any) => {
      let foundCancel = false;
      shifts.forEach((s2) => {
        if (s?.shiftId === s2?.shiftReferenceId && s2?.shiftDate === selectedDateFormatted) {
          foundCancel = true;
        }
      });

      return (
        (s?.shiftDate === selectedDateFormatted || (s?.isRecurring && s?.daysOfWeek?.includes(selectedDayOfWeek))) &&
        !s?.cancelShiftId &&
        !foundCancel
      );
    });

    setShiftsForDay(filteredShifts);
  }, [selectedDate, shifts]);

  // Update daysOfWeek when selectedDayForAssignment changes and modal opens
  React.useEffect(() => {
    if (selectedDayForAssignment && showAddShiftModal) {
      const dayOfWeek = selectedDayForAssignment.format('dddd').toLowerCase();
      setAddShiftFormData(prev => ({
        ...prev,
        daysOfWeek: [dayOfWeek]
      }));
    }
  }, [selectedDayForAssignment, showAddShiftModal]);

  const getNextRecurringShiftDate = (daysOfWeek: string[]): moment.Moment => {
    if (!daysOfWeek || daysOfWeek.length === 0) {
      return moment().add(1, 'day'); // Default to tomorrow if no days specified
    }

    const today = moment();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Convert day names to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayNumbers = daysOfWeek.map(day => dayNames.indexOf(day.toLowerCase())).filter(num => num !== -1);

    if (dayNumbers.length === 0) {
      return moment().add(1, 'day'); // Default if no valid days found
    }

    // Sort day numbers to find the next occurrence
    dayNumbers.sort((a, b) => a - b);

    const currentDayNumber = today.day();

    // Find the next day in the same week
    const nextDayInWeek = dayNumbers.find(dayNum => dayNum > currentDayNumber);

    if (nextDayInWeek !== undefined) {
      // Next occurrence is in this week
      return moment().day(nextDayInWeek);
    } else {
      // Next occurrence is in the following week - take the first day from the list
      return moment().add(1, 'week').day(dayNumbers[0]);
    }
  };

  const upcomingShifts = shifts.filter(shift => {
    let shiftDate = shift.isRecurring ? getNextRecurringShiftDate(shift.daysOfWeek) : moment(shift.shiftDate);
    shiftDate.set("hour", moment(shift.startTime, "HH:mm").hour());
    shiftDate.set("minute", moment(shift.startTime, "HH:mm").minute());
    let assignees: string[] = shift.assignedStaff
    assignments.map((assignment) => {
      if (assignment.shiftId === shift.shiftId && moment(assignment.date).isSame(shiftDate, 'day')) {
        assignees = assignment.assignees;
      }
    })
    return shiftDate.isSameOrAfter(moment()) && assignees?.includes(userData.userId);
  }).sort((a, b) => {
    const dateA = a.isRecurring ? getNextRecurringShiftDate(a.daysOfWeek) : moment(a.shiftDate);
    const dateB = b.isRecurring ? getNextRecurringShiftDate(b.daysOfWeek) : moment(b.shiftDate);
    return dateA.diff(dateB);
  });

  const weekStats = {
    totalHours: 40,
    shiftsScheduled: 5,
    daysOff: 2
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDeleteShift = (scheduleId: string, shiftId: string) => {
    deleteShift(scheduleId, shiftId).then(() => {
      if (selectedSchedule) {
        getShifts(selectedSchedule.scheduleId).then((response) => {
          setShifts(response)
        })
      }
    });

    handleMenuClose();
  };

  const takeActionOnRequest = (request: BaseRequest) => {
    try {
      if (request.requestType === "SWAP") {
        if (request.status === "approved") {
          request.requestDetails.swapTarget = getUserPool()!.getCurrentUser()!.getUsername();
          getAssignments(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate).then((response: any[]) => {
            if (response && response.length === 0) {
              getShift(request.scheduleId, request.requestDetails.shiftId).then((shift_response) => {
                if (shift_response && shift_response.length > 0) {
                  putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate, [...shift_response[0].assignedStaff.filter((assignee: string) => assignee !== request.requesterId),
                  request.requestDetails.swapTarget])
                } else {
                  toast.showError("Error processing swap request: Shift not found.");
                }
              });
            } else {
              putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate, [...response[0].assignees, request.requestDetails.swapTarget])
            }
          })
        } else {
          getAssignments(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate).then((response: any[]) => {
            if (response && response.length === 0) {
              getShift(request.scheduleId, request.requestDetails.shiftId).then((shift_response) => {
                if (shift_response && shift_response.length > 0) {
                  putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate, [...shift_response[0].assignedStaff.filter((assignee: string) => assignee !== request.requesterId),
                  request.requestDetails.swapTarget])
                } else {
                  toast.showError("Error processing swap request: Shift not found.");
                }
              });
            } else {
              putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate, [...response[0].assignees, request.requestDetails.swapTarget])
            }
          })
        }
      } else if (request.requestType === "OFF") {
        getAssignments(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.swapDate).then((response: any[]) => {
          if (response == null || response?.length === 0) {
            getShift(request.scheduleId, request.requestDetails.shiftId).then((shift_response) => {
              if (shift_response && shift_response.length > 0) {
                putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.offDate,
                  [...shift_response[0].assignedStaff.filter((assignee: string) => assignee !== request.requesterId)])
              } else {
                toast.showError("Error processing time off request: Shift not found.");
              }
            });
          } else {
            putAssignment(request.scheduleId, request.requestDetails.shiftId, request.requestDetails.offDate, [...response[0].assignees.filter((assignee: string) => assignee !== request.requesterId)])
          }
        })
      } else if (request.requestType === "JOIN") {
        putScheduleUser(request.requesterId, selectedSchedule?.scheduleId + "#" + selectedSchedule?.userId, request.requesterName, "staff")
        let scheduleTmp = JSON.parse(JSON.stringify(selectedSchedule)) as Schedule;
        scheduleTmp.employees.push(request.requesterId || "")
        putSchedule(scheduleTmp.userId, scheduleTmp.scheduleId, scheduleTmp.name,
          scheduleTmp.color, scheduleTmp.link, scheduleTmp.employees,
          scheduleTmp.favorite, scheduleTmp.allowSwapWithoutApproval,
          scheduleTmp.allowPickUpWithoutApproval, scheduleTmp.viewOtherStaff,
          scheduleTmp.activeScheduleFrequency, scheduleTmp.lastActiveScheduleDate,
          scheduleTmp.availabilityScheduleRequiredTime, scheduleTmp.activeScheduleDate,
          scheduleTmp.lastSubmitted
        )
      }
    } catch (error) {
      toast.showError("Error processing request");
      throw error;
    }
  }

  const handleRemoveEmployee = async (scheduleId: string, selectedEmployee: Record<string, any>) => {
    try {
      await deleteScheduleUser(selectedEmployee.userId, scheduleId + "#" + selectedSchedule?.userId);
      await Promise.all(positions.map(async (position) => {
        if (position.employees?.includes(selectedEmployee.userId)) {
          const updatedEmployees = position.employees.filter((empId: string) => empId !== selectedEmployee.userId);
          await putPosition(selectedSchedule?.scheduleId!, position.position, position.positionName, updatedEmployees);
        }
      }))
      await Promise.all(shifts.map(async (shift) => {
        if (shift.assignedStaff?.includes(selectedEmployee.userId)) {
          const updatedEmployees = shift.assignedStaff.filter((empId: string) => empId !== selectedEmployee.userId);
          await putShift(selectedSchedule?.scheduleId!, shift.shiftId, shift.shiftName, shift.daysOfWeek, shift.startTime, shift.endTime,
            shift.position, shift.staffNeeded, updatedEmployees, shift.isRecurring, shift.shiftDate, shift.notes);
        }
      }))
      await deleteUserNotificationSettings(selectedEmployee.userId);
      await putUser(selectedEmployee.userId, selectedEmployee.accountType, selectedEmployee.name, selectedEmployee.email, selectedEmployee.phoneNumber, selectedEmployee.created, moment().format("YYYY-MM-DD"));
      await putSchedule(scheduleId + "#" + selectedSchedule?.userId, scheduleId, selectedSchedule!.name,
        selectedSchedule!.color, selectedSchedule!.link, employees.filter(emp => emp.userId !== selectedEmployee.userId).map(emp => emp.userId),
        selectedSchedule!.favorite, selectedSchedule!.allowSwapWithoutApproval,
        selectedSchedule!.allowPickUpWithoutApproval, selectedSchedule!.viewOtherStaff,
        selectedSchedule!.activeScheduleFrequency, selectedSchedule!.lastActiveScheduleDate, selectedSchedule!.availabilityScheduleRequiredTime,
        selectedSchedule!.activeScheduleDate, selectedSchedule!.lastSubmitted);
      toast.showSuccess('Employee removed successfully');
    } catch (error) {
      toast.showError('Failed to remove employee');
      throw error;
    }
  };

  const handleUpdateAccessLevel = async (scheduleId: string, userId: string, accessLevel: string, name: string) => {
    try {
      await putScheduleUser(userId, scheduleId + "#" + selectedSchedule?.userId, name, accessLevel);
      toast.showSuccess('Access level updated successfully');
    } catch (error) {
      toast.showError('Failed to update access level');
      throw error;
    }
  };

  const handleRefresh = async (selectedEmployee: Employee, newAccessLevel?: string) => {
    // Reload employee list
    setEmployees((prev) => prev.map(emp => emp.userId === selectedEmployee.userId ? { ...emp, accessLevel: newAccessLevel as 'admin' | 'manager' | 'staff' } : emp));
  };

  const handleSubmitSchedule = async () => {
    try {
      if (selectedSchedule) {
        const today = moment();
        await putSchedule(selectedSchedule.userId, selectedSchedule.scheduleId, selectedSchedule.name,
          selectedSchedule.color, selectedSchedule.link, selectedSchedule.employees,
          selectedSchedule.favorite, selectedSchedule.allowSwapWithoutApproval,
          selectedSchedule.allowPickUpWithoutApproval, selectedSchedule.viewOtherStaff,
          selectedSchedule.activeScheduleFrequency, selectedSchedule.lastActiveScheduleDate,
          selectedSchedule.availabilityScheduleRequiredTime, selectedSchedule.activeScheduleDate,
          today.format("YYYY-MM-DD"));
        await submitSchedule(selectedSchedule.userId, selectedSchedule.scheduleId);
        await getSchedules(getUserPool()!.getCurrentUser()!.getUsername()).then((response) => {
          setSelectedSchedule(response.flat()[0])
        })
        setShowDeadline(false);
        toast.showSuccess('Schedule submitted successfully');
      }
    } catch (error) {
      toast.showError('Failed to submit schedule');
      throw error;
    }
  };

  const getAvailabilityColor = (isDarkMode: boolean, currentDate: string) => {
    const unavailable = isDarkMode ? darkTheme.palette.error.main : lightTheme.palette.error.light;
    const halfavailable = isDarkMode ? darkTheme.palette.warning.light : darkTheme.palette.warning.light;
    const available = isDarkMode ? darkTheme.palette.success.main : darkTheme.palette.success.main;
    let color = unavailable;

    [...availabilities, ...defaultAvailabilities].forEach((availability) => {
      if ((availability.dateUserId && availability.dateUserId.split("#")[0] === currentDate) ||
        (availability.dayUserId && availability.dayUserId.split("#")[1] === moment(currentDate).format("ddd"))) {
        if (userData.userId === availability.userId) {
          let count = 0;
          Object.values(availability.availability).forEach((timeSlot: any) => {
            if (timeSlot?.includes("30")) {
              if (color !== available) {
                count += 30
              }
            } else {
              count += 60
            }
          })
          color = count / 60 < 7.5 ? halfavailable : available;
        }
      }
    })

    if (color !== unavailable) {
      return color;
    } else {
      if (isDarkMode) {
        return darkTheme.palette.error.main;
      } else {
        return unavailable;
      }
    }
  };

  React.useEffect(() => {
    if (accessLevel === 'staff' && selectedSchedule && defaultAvailabilities.length > 0) {
      // Check if all days have been set
      const availability = defaultAvailabilities.filter((da) => da.userId === userData.userId
        && JSON.stringify(da.availability) !== "{}")

      if (availability.length < 1) {
        // User hasn't set all days, show modal
        setShowIncompleteAvailabilityModal(true);
      }
    }
  }, [accessLevel, selectedSchedule, defaultAvailabilities]);

  const handleIncompleteAvailabilitySubmit = async (availabilities: Record<string, string>) => {
    const dayMap: Record<string, string> = {
      Sunday: 'Sun',
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat'
    };

    try {
      await Promise.all(
        Object.entries(availabilities).map(async ([day, availability]) => {
          if (availability !== 'Unavailable') {
            // Build availability object based on selection
            let availabilityObj: Record<string, any> = {};

            if (availability === 'Morning (5am - 12pm)') {
              for (let i = 5; i <= 12; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
            } else if (availability === 'Afternoon (12pm - 5pm)') {
              for (let i = 12; i <= 17; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
            } else if (availability === 'Evening (5pm - 10pm)') {
              for (let i = 17; i <= 22; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
            } else if (availability === 'Night (10pm - 5am)') {
              for (let i = 0; i <= 5; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
              for (let i = 22; i <= 23; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
            } else if (availability === 'All Day (9am - 5pm)') {
              for (let i = 9; i <= 17; i++) {
                let hourString = i < 10 ? `0${i}:00` : `${i}:00`;
                let hourString2 = i + 1 < 10 ? `0${i + 1}:00` : `${i + 1}:00`;
                availabilityObj[i] = `${hourString}-${hourString2}`;
              }
            }

            await putDefaultAvailability(
              selectedSchedule!.scheduleId,
              userData.userId + '#' + dayMap[day],
              JSON.stringify(availabilityObj),
              userData.userId,
              dayMap[day]
            );
          }
        })
      );

      toast.showSuccess('Default availability updated successfully');
      setShowIncompleteAvailabilityModal(false);
    } catch (error) {
      toast.showError('Error saving availability');
    }
  };

  const handleSubmitReview = async (review: any) => {
    try {
      await putReview(
        userData.userId,
        moment().toISOString(),
        review.rating,
        review.comment,
        userData.accountType || "employee",
        userData.name
      );

      // Clear the dismissal flag since user submitted a review
      sessionStorage.removeItem(`reviewModalDismissed_${userData.userId}`);
      setShowRequestReviewModal(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.showError('Error submitting review');
      throw error;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Sidebar */}
      <DashboardSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        setSelectedSchedule={setSelectedSchedule}
        selectedSchedule={selectedSchedule}
        userData={userData}
        accessLevel={accessLevel}
        shifts={shifts}
      />

      {/* Header */}
      <MainHeader
        title={`${selectedSchedule?.name || 'No Schedule'}`}
        subTitle={`Dashboard - ${getAuth().name || ''}`}
        onClickLogo={() => navigate('/')}
        onClickProfile={() => navigate('/profile')}
        onClickLogout={() => {
          logout();
          navigate('/login');
        }}
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Main Schedule Section */}
          <Grid item xs={12} lg={9}>
            <Paper elevation={1} sx={{ borderRadius: 2 }}>
              {/* View Controls */}
              <ScheduleViewControls
                viewMode={viewMode}
                setViewMode={setViewMode}
                currentWeek={currentWeek}
                setCurrentWeek={setCurrentWeek}
                isDarkMode={isDarkMode}
                setAvailabilityMode={setAvailabilityMode}
                availabilityMode={availabilityMode}
              />

              {/* Legend */}
              <ScheduleLegend
                isDarkMode={isDarkMode}
                accountType={checkAccessLevel(accessLevel) ? "owner" : "employee"}
                availabilityMode={availabilityMode}
                legendFilters={legendFilters}
                onToggleFilter={toggleLegendFilter}
                selectedSchedule={selectedSchedule}
              />

              <Divider sx={{ p: 2, width: '45%', mx: 'auto' }} variant='middle' />

              {/* Schedule Views */}
              <Box sx={{ minHeight: 400 }}>
                {/* Week View */}
                {viewMode === 'week' && (
                  <Box sx={{ p: 3 }}>
                    <WeekView
                      daysOfTheCurrentWeek={daysOfTheCurrentWeek}
                      filteredShifts={filteredShifts}
                      assignments={assignments}
                      positions={positions}
                      holidays={holidays}
                      availabilityMode={availabilityMode}
                      isDarkMode={isDarkMode}
                      selectedSchedule={selectedSchedule}
                      accessLevel={accessLevel}
                      getAvailabilityColor={getAvailabilityColor}
                      shouldShowDay={shouldShowDay}
                      onShiftClick={(shift, dayDate) => {
                        setSelectedShift(shift);
                        setSelectedDayForAssignment(dayDate);
                        setSelectedDate(dayDate);
                        setDayModalOpen(true);
                      }}
                      onAddShiftClick={(date) => {
                        setSelectedDayForAssignment(date);
                        setShowAddShiftModal(true);
                      }}
                    />
                  </Box>
                )}

                {/* Month View - Enhanced */}
                {viewMode === 'month' && (
                  <Box sx={{ p: 3, pl: 0 }}>
                    <MonthView
                      currentWeek={currentWeek}
                      filteredShifts={filteredShifts}
                      assignments={assignments}
                      positions={positions}
                      holidays={holidays}
                      availabilityMode={availabilityMode}
                      isDarkMode={isDarkMode}
                      selectedSchedule={selectedSchedule}
                      accessLevel={accessLevel}
                      getAvailabilityColor={getAvailabilityColor}
                      shouldShowDay={shouldShowDay}
                      onShiftClick={(shift, dayDate) => {
                        setSelectedShift(shift);
                        setSelectedDayForAssignment(dayDate);
                        setSelectedDate(dayDate);
                        setDayModalOpen(true);
                      }}
                      onDateClick={(date, isAvailabilityMode) => {
                        if (isAvailabilityMode) {
                          if (date >= moment().subtract(2, 'day')) {
                            setSelectedDayForAssignment(date);
                            setShowAvailabilityModal(true);
                          }
                        } else {
                          setSelectedShift(null);
                          setSelectedDayForAssignment(date);
                          setSelectedDate(date);
                          setDayModalOpen(true);
                        }
                      }}
                      onAddShiftClick={(date) => {
                        setSelectedDayForAssignment(date);
                        addShiftFormData.daysOfWeek = [date.format('dddd').toLowerCase()];
                        addShiftFormData.shiftDate = date.format('YYYY-MM-DD');
                        setAddShiftFormData({ ...addShiftFormData });
                        setShowAddShiftModal(true);
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={3} sx={{ mt: { xs: 2, lg: 0 } }}>
            <QuickActionSidebar
              upcomingShifts={upcomingShifts}
              nextShiftExpanded={nextShiftExpanded}
              selectedSchedule={selectedSchedule}
              setNextShiftExpanded={setNextShiftExpanded}
              getNextRecurringShiftDate={getNextRecurringShiftDate}
              weekStats={weekStats}
              userData={userData}
              requests={requests}
              accessLevel={accessLevel}
              quickActions={[
                checkAccessLevel(accessLevel) && {
                  title: 'Add Shift',
                  description: 'Create a new shift for your schedule',
                  icon: Add,
                  onClick: () => setShowAddShiftModal(true),
                  color: 'secondary' as const
                },
                checkAccessLevel(accessLevel) && {
                  title: 'Invite Staff',
                  description: 'Send an invitation to a new staff member',
                  icon: PersonAdd,
                  onClick: () => setShowInviteModal(true),
                  color: 'secondary' as const
                },
                checkAccessLevel(accessLevel) && {
                  title: 'Manage Positions',
                  description: 'Manage different positions for your business',
                  icon: Work,
                  onClick: () => setShowManagePositionsModal(true),
                  color: 'secondary' as const
                },
                checkAccessLevel(accessLevel) && {
                  title: 'Manage Staff',
                  description: 'Remove and manage staff access',
                  icon: Group,
                  onClick: () => setShowManageEmployees(true),
                  color: 'secondary' as const
                },
                {
                  title: 'Manage Requests',
                  description: 'Manage requests',
                  icon: Group,
                  onClick: () => setApprovalModalOpen(true),
                  color: 'secondary' as const
                },
                {
                  title: 'Update Default Availability',
                  description: 'Update your default availability',
                  icon: Group,
                  onClick: () => setShowDefaultAvailabilityModal(true),
                  color: 'secondary' as const
                },
              ].filter(Boolean) as Array<{ title: string; description: string; icon: any; color: "primary" | "success" | "info" | "secondary" | "error" | "warning"; onClick: () => void; }>}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Modals */}
      <ScheduleDeadlineSnackbar
        open={showDeadline}
        onClose={() => setShowDeadline(false)}
        onSubmitSchedule={() => handleSubmitSchedule()}
        showCountdown={false}
        schedule={selectedSchedule}
      />
      <AvailabilitySubmissionModal
        open={showAvailabilityModal}
        currentDate={selectedDayForAssignment ? selectedDayForAssignment.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}
        availability={availabilities.find((v: Record<string, any>) => v.dateUserId.split("#")[0] === (selectedDayForAssignment ? selectedDayForAssignment.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')))?.availability || {}} // Example availability data
        defaultAvailabilities={defaultAvailabilities}
        onClose={() => setShowAvailabilityModal(false)}
        setShowDefaultAvailabilityModal={(value: boolean) => setShowDefaultAvailabilityModal(value)}
        onSubmit={async (newAvailability, currentDate) => {
          try {
            await putAvailability(selectedSchedule!.scheduleId, currentDate + "#" + userData.userId, JSON.stringify(newAvailability),
              userData.userId, currentDate
            );
            setAvailabilities((prevAvailabilities) => {
              const otherAvailabilities = prevAvailabilities.filter((v: Record<string, any>) => v.dateUserId.split("#")[0] !== currentDate);
              return [...otherAvailabilities, { dateUserId: currentDate + "#" + userData.userId, availability: newAvailability }];
            });
            toast.showSuccess('Availability updated successfully');
          } catch (error) {
            toast.showError('Failed to update availability');
            throw error;
          }
        }}
      />

      <DefaultAvailabilitySubmissionModal
        open={showDefaultAvailabitlityModal}
        currentDate={selectedDayForAssignment ? selectedDayForAssignment.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}
        defaultAvailabilities={defaultAvailabilities.filter((v: Record<string, any>) => v.userId === userData.userId)}
        onClose={() => setShowDefaultAvailabilityModal(false)}
        onSubmit={async (newAvailability, defaultDay) => {
          try {
            await putDefaultAvailability(selectedSchedule!.scheduleId, userData.userId + "#" + defaultDay, JSON.stringify(newAvailability), userData.userId, defaultDay);
            setDefaultAvailabilities((prevDefaultAvailabilities) => {
              const otherDefaultAvailabilities = prevDefaultAvailabilities.filter((v: Record<string, any>) => v.dayUserId !== userData.userId + "#" + defaultDay);
              return [...otherDefaultAvailabilities, { dayUserId: userData.userId + "#" + defaultDay, 
                availability: newAvailability, userId: userData.userId, defaultDay: defaultDay, scheduleId: selectedSchedule!.scheduleId}];
            });
            toast.showSuccess('Default availability saved successfully');
          } catch (error) {
            toast.showError('Failed to save default availability');
            throw error;
          }
        }}
      />

      <ManageEmployeesModal
        open={showManageEmployees}
        onClose={() => setShowManageEmployees(false)}
        selectedSchedule={selectedSchedule}
        employees={employees}
        onRemoveEmployee={handleRemoveEmployee}
        onUpdateAccessLevel={handleUpdateAccessLevel}
        onRefresh={handleRefresh}
      />
      <ManageShiftModal
        open={showManageShiftModal}
        onClose={() => {
          setShowManageShiftModal(false);
          if (validateAuth() && selectedSchedule) {
            getShifts(selectedSchedule.scheduleId).then((response) => {
              setShifts(response);
            });
          }
        }}
        availabilities={availabilities}
        defaultAvailabilities={defaultAvailabilities}
        shift={selectedShift}
        selectedDay={selectedDayForAssignment}
        selectedSchedule={selectedSchedule}
        positions={positions}
        shifts={shifts}
        setShifts={setShifts}
        assignments={assignments}
        setAssignments={setAssignments}
        userData={userData}
        accessLevel={accessLevel}
      />
      <AddShiftModal
        open={showAddShiftModal}
        onClose={() => setShowAddShiftModal(false)}
        selectedSchedule={selectedSchedule}
        addShiftFormData={addShiftFormData}
        setAddShiftFormData={setAddShiftFormData}
        shifts={shifts}
        setShifts={setShifts}
        positions={positions}
        setPositions={setPositions}
      />
      <ManagePositionsModal
        open={showManagePositionsModal}
        onClose={() => setShowManagePositionsModal(false)}
        selectedSchedule={selectedSchedule}
        userData={userData}
      />
      <InviteEmployeeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        selectedSchedule={selectedSchedule}
      />
      <EmployeeDayModal
        open={dayModalOpen}
        onClose={() => setDayModalOpen(false)}
        date={selectedDate.format('YYYY-MM-DD')} // ISO date string
        shifts={shiftsForDay} // Array of shifts matching your structure
        allShifts={shifts as any[]}
        currentEmployeeId={userData.userId}
        userData={userData}
        selectedShift={selectedShift}
        setShifts={setShifts}
        setShiftsForDay={setShiftsForDay}
        setSelectedShift={setSelectedShift}
        setShowManageShiftModal={setShowManageShiftModal}
        employees={employees}
        positions={positions}
        assignments={assignments}
        onPickUpShift={(shiftId) => {
          assignments.map((assignment) => {
            let foundCustomAssignment: any = null;
            if (assignment.shiftId === shiftId && assignment.date === selectedDate.format('YYYY-MM-DD')) {
              foundCustomAssignment = assignment;
            }

            if (foundCustomAssignment) {
              putAssignment(selectedSchedule!.scheduleId, shiftId, selectedDate.format('YYYY-MM-DD'), [...foundCustomAssignment.assignees, userData.userId]).then(() => {
              });
            } else {
              putAssignment(selectedSchedule!.scheduleId, shiftId, selectedDate.format('YYYY-MM-DD'), [...selectedShift.assignedStaff, userData.userId]).then(() => {
              });
            }

            setShifts(prevShifts => prevShifts.map(shift => {
              if (shift.shiftId === shiftId) {
                return {
                  ...shift,
                  assignedStaff: [...shift.assignedStaff, userData.userId]
                };
              }
              return shift;
            }));

            setAssignments(prevAssignments => {
              if (foundCustomAssignment) {
                return prevAssignments.map(assignment => {
                  if (assignment.shiftId === shiftId && assignment.date === selectedDate.format('YYYY-MM-DD')) {
                    return {
                      ...assignment,
                      assignees: [...assignment.assignees, userData.userId]
                    };
                  }
                  return assignment;
                });
              } else {
                return [...prevAssignments, {
                  shiftId: shiftId,
                  date: selectedDate.format('YYYY-MM-DD'),
                  assignees: [...selectedShift.assignedStaff, userData.userId]
                }];
              }
            });
          })
        }}
        onRequestSwap={(shiftId, targetId, reason, isPermanent, swapDate) => {
          try {
            let requestDetails = {
              swapDuration: isPermanent,
              swapReason: reason,
              swapTarget: targetId,
              swapDate: swapDate,
              shiftId: shiftId
            }
            const requestId = uuidv4();
            const newRequest: BaseRequest = {
              scheduleId: selectedSchedule?.scheduleId!,
              requestId: requestId,
              requestType: 'SWAP',
              requesterId: userData.userId,
              requesterName: userData.name,
              dateTimeRequested: moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
              requestDetails: requestDetails,
              status: 'pending'
            };

            if (!selectedSchedule?.allowSwapWithoutApproval) {
              putRequest(selectedSchedule?.scheduleId!, requestId, 'SWAP', userData.userId, userData.name, moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
                JSON.stringify(requestDetails), 'pending').then(() => {
                  setRequests(prevRequests => prevRequests ? [...prevRequests, newRequest as Request] : [newRequest as Request]);
                  toast.showSuccess("Shift swap submitted for approval")
                  putNotificationRequest(userData.userId, selectedSchedule?.scheduleId!, "approvalsNotification")
                })
            } else {
              takeActionOnRequest(newRequest);
              setRequests(prevRequests => prevRequests ? [...prevRequests, newRequest as Request] : [newRequest as Request]);
              toast.showSuccess("Shift swap completed successfully")
            }
          } catch (e) {
            toast.showError("Error occured while submitting shift swap")
          }
        }
        }
        onRequestTimeOff={(shiftId, date, reason) => {
          try {
            let requestDetails = {
              offReason: reason,
              offDate: date,
              shiftId: shiftId
            }
            const requestId = uuidv4();
            const newRequest: BaseRequest = {
              scheduleId: selectedSchedule?.scheduleId!,
              requestId: requestId,
              requestType: 'OFF',
              requesterId: userData.userId,
              requesterName: userData.name,
              dateTimeRequested: moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
              requestDetails: requestDetails,
              status: 'pending'
            };

            putRequest(selectedSchedule?.scheduleId!, requestId, 'OFF', userData.userId, userData.name, moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
              JSON.stringify(requestDetails), 'pending').then(() => {
                setRequests(prevRequests => prevRequests ? [...prevRequests, newRequest as Request] : [newRequest as Request]);
                putNotificationRequest(userData.userId, selectedSchedule?.scheduleId!, "approvalsNotification")
                toast.showSuccess("Time off request submitted for approval")
              })
          } catch (e) {
            toast.showError("Error occurred while submitting time off request")
          }
        }}
        selectedSchedule={selectedSchedule}
        accessLevel={accessLevel}
      />
      <ManagerApprovalModal
        open={approvalModalOpen}
        userData={userData}
        onClose={() => setApprovalModalOpen(false)}
        onApprove={async (request, note) => {
          try {
            takeActionOnRequest(request)
            if (request.requestType === "JOIN") {
              putInviteRequest(request.scheduleId, request.requestId, request.requestType, request.requesterId, request.requesterName,
                request.dateTimeRequested, JSON.stringify(request.requestDetails), "approved", getUserPool()?.getCurrentUser()?.getUsername())
              putNotificationRequest(request.requesterId, request.scheduleId, "newMemberAccept")
            } else {
              putRequest(request.scheduleId, request.requestId, request.requestType, request.requesterId, request.requesterName,
                request.dateTimeRequested, JSON.stringify(request.requestDetails), "approved", getUserPool()?.getCurrentUser()?.getUsername())
            }

            // Update local state
            setRequests(prevRequests => prevRequests ? prevRequests.map(r => r.requestId === request.requestId ? { ...r, status: 'approved' } : r) : null);

            toast.showSuccess("Request approved successfully");
          } catch {
            toast.showError("Error approving request")
          }
          setApprovalModalOpen(false);
        }}
        onDeny={async (request, reason) => {
          try {
            if (request.requestType === "JOIN") {
              putInviteRequest(request.scheduleId, request.requestId, request.requestType, request.requesterId, request.requesterName,
                request.dateTimeRequested, JSON.stringify(request.requestDetails), "deny", getUserPool()?.getCurrentUser()?.getUsername())
              putNotificationRequest(request.requesterId, request.scheduleId, "newMemberDeny")
            } else {
              putRequest(request.scheduleId, request.requestId, request.requestType, request.requesterId, request.requesterName,
                request.dateTimeRequested, JSON.stringify(request.requestDetails), "deny", getUserPool()?.getCurrentUser()?.getUsername())
            }

            // Update local state
            setRequests(prevRequests => prevRequests ? prevRequests.map(r => r.requestId === request.requestId ? { ...r, status: 'deny' } : r) : null);

            toast.showSuccess("Request denied successfully");
          } catch {
            toast.showError("Error denying request")
          }
          setApprovalModalOpen(false);
        }}
        onPickUpShift={async (request) => {
          try {
            takeActionOnRequest(request);
            putRequest(request.scheduleId, request.requestId, request.requestType, request.requesterId, request.requesterName,
              request.dateTimeRequested, JSON.stringify(request.requestDetails), "pickedup", getUserPool()?.getCurrentUser()?.getUsername())
              
            // Update local state
            setRequests(prevRequests => prevRequests ? prevRequests.map(r => r.requestId === request.requestId ? { ...r, status: 'pickedup' } : r) : null);

            toast.showSuccess("Shift picked up successfully");
          } catch (error) {
            toast.showError("Error picking up shift");
          }
        }}
        schedule={selectedSchedule}
        positions={positions as any}
        employees={employees}
        requests={requests}
      />
      <SwapShiftModal
        open={showSwapModal}
        onClose={() => { setShowSwapModal(false); setSelectedShift(null); }}
        shift={selectedShift}
        currentEmployeeId={userData.userId}
        employees={employees}
        positions={positions}
        onSubmitSwapRequest={(shiftId, targetEmployeeId, reason) =>
          console.log('Swap request submitted:', shiftId, targetEmployeeId, reason)
        }
        selectedSchedule={selectedSchedule}
        selectedDate={selectedDate.format('YYYY-MM-DD')}
      />
      <RequestOffModal
        open={showRequestOffModal}
        onClose={() => setShowRequestOffModal(false)}
        shift={selectedShift}
        currentEmployeeId={userData.userId}
        employees={employees}
        positions={positions}
        availableShifts={shifts as any}
        onSubmitRequestOff={(shiftId, offDate, offReason) => {
          try {
            if (!selectedSchedule?.allowSwapWithoutApproval) {
              const { v4: uuidv4 } = require('uuid');
              let requestDetails = {
                offReason: offReason,
                offDate: offDate,
                shiftId: shiftId
              }
              putRequest(selectedSchedule?.scheduleId!, uuidv4(), 'OFF', userData.userId, userData.name, moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
                JSON.stringify(requestDetails), 'pending').then(() => {
                  toast.showSuccess("Time off request submitted for approval")
                })
              putNotificationRequest(userData.userId, selectedSchedule?.scheduleId!, "approvalsNotification")
            }
          } catch (e) {
            toast.showError("Error occurred while submitting time off request")
          }
        }}
        selectedSchedule={selectedSchedule}
        selectedDate={selectedDate.format('YYYY-MM-DD')}
      />
      {/* Context Menu - Update to set selected day */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          // Set the selected day based on current view and selected shift
          if (viewMode === 'week') {
            // Find which day this shift belongs to in the current week
            const shiftDay = daysOfTheCurrentWeek.find(day =>
              selectedShift?.daysOfWeek?.includes(day.format('dddd').toLowerCase()) ||
              selectedShift?.shiftDate === day.format('YYYY-MM-DD')
            );
            if (shiftDay) {
              setSelectedDayForAssignment(shiftDay);
            }
          } else {
            // For month view, find the day in the current month
            const firstDayOfMonth = moment(currentWeek).startOf('month');
            const lastDayOfMonth = moment(currentWeek).endOf('month');

            if (selectedShift?.shiftDate) {
              const shiftDate = moment(selectedShift.shiftDate);
              if (shiftDate.isBetween(firstDayOfMonth, lastDayOfMonth, 'day', '[]')) {
                setSelectedDayForAssignment(shiftDate);
              }
            } else if (selectedShift?.daysOfWeek) {
              // For recurring shifts, use the first occurrence in the current month
              const dayName = selectedShift.daysOfWeek[0];
              let dayOfWeek = moment().day(dayName).day(); // Get numeric day (0=Sun, 1=Mon, etc.)
              let firstOccurrence = moment(firstDayOfMonth).day(dayOfWeek);

              if (firstOccurrence.isBefore(firstDayOfMonth)) {
                firstOccurrence.add(1, 'week');
              }

              if (firstOccurrence.isBetween(firstDayOfMonth, lastDayOfMonth, 'day', '[]')) {
                setSelectedDayForAssignment(firstOccurrence);
              }
            }
          }

          setShowManageShiftModal(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Shift</ListItemText>
        </MenuItem>

        {checkAccessLevel(accessLevel) && (
          <MenuItem onClick={() => {
            if (selectedShift && selectedSchedule) {
              handleDeleteShift(selectedSchedule.scheduleId, selectedShift.shiftId);
            }
          }}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Shift</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <IncompleteDefaultAvailabilityModal
        open={showIncompleteAvailabilityModal}
        onClose={() => setShowIncompleteAvailabilityModal(false)}
        onSubmit={handleIncompleteAvailabilitySubmit}
        defaultAvailabilities={defaultAvailabilities.filter((v: Record<string, any>) => v.userId === userData.userId)}
      />
      <SubmitReviewModal
        open={showRequestReviewModal}
        onClose={() => setShowRequestReviewModal(false)}
        onSubmit={handleSubmitReview}
        userId={userData.userId}
      />
    </Box >
  );
}
